import type { Nullable } from 'types';
import type { BannerImagesData } from 'utils/clickme';
import {
    CampaignDeviceType,
    CampaignGender,
    CampaignLanguage,
    CampaignProductType,
    CampaignSource,
    CampaignSubSource,
    CampaignSubSourceDisableReason,
    CampaignType,
} from 'utils/constants/campaign';

import { CampaignFrontendStatus } from 'store/models/campaignsDashboard';
import { BannerVideo } from 'store/models/createBanner';
import type { SalaryRange } from 'store/models/createCampaign';

export enum CampaignFields {
    FrontendStatus = 'frontendStatus',
    Banner = 'banner',
    CampaignTitle = 'title',
    Sex = 'sex',
    AgeMin = 'ageMin',
    AgeMax = 'ageMax',
    Sources = 'campaignSources',
    Budget = 'budget',
    BudgetDaily = 'budgetDaily',
    CampaignId = 'id',
    ProfessionalRoles = 'professionalRoles',
    Areas = 'areas',
    VacancyId = 'vacancyId',
    SalaryRange = 'salaryRange',
    Language = 'language',
    DeviceType = 'deviceType',
    IncludeAnonymousAge = 'includeAnonymousAge',
    AutoSource = 'autoSource',
    Bid = 'bid',
    ApiSources = 'apiSources',
    SmallImgAllowed = 'smallImgAllowed',
    ProductType = 'productType',
    AdvertisingServiceType = 'advertisingServiceType',
}

export interface CampaignSources {
    apiSource: CampaignSource;
    subSources: CampaignSubSource[];
}

export interface AreasDto {
    include: number[];
    exclude: number[];
}

export interface CampaignCommon {
    [CampaignFields.Areas]: AreasDto;
    [CampaignFields.Sex]: CampaignGender;
    [CampaignFields.AgeMin]: number;
    [CampaignFields.AgeMax]: number;
    [CampaignFields.Budget]: Nullable<string>;
    [CampaignFields.BudgetDaily]: Nullable<string>;
    [CampaignFields.CampaignTitle]: string;
    [CampaignFields.SalaryRange]: Nullable<SalaryRange>;
    [CampaignFields.Language]: CampaignLanguage[];
    [CampaignFields.DeviceType]: CampaignDeviceType;
    [CampaignFields.IncludeAnonymousAge]: boolean;
    [CampaignFields.VacancyId]: Nullable<number>;
    [CampaignFields.AutoSource]: boolean;
    [CampaignFields.Bid]: Nullable<number>;
    [CampaignFields.ProfessionalRoles]?: string[];
    [CampaignFields.Sources]: CampaignSources[];
}

export interface Campaign extends CampaignCommon {
    [CampaignFields.FrontendStatus]: CampaignFrontendStatus;
    [CampaignFields.CampaignId]: Nullable<number>;
    [CampaignFields.Banner]: Banner;
    [CampaignFields.ApiSources]: CampaignSubSource[];
    [CampaignFields.SmallImgAllowed]: boolean;
    [CampaignFields.ProductType]: CampaignProductType;
    [CampaignFields.AdvertisingServiceType]: CampaignType;
}

export interface EditCampaignData extends CampaignCommon {
    [CampaignFields.CampaignId]: Nullable<number>;
}

export interface CreateCampaignData extends CampaignCommon {
    [CampaignFields.Banner]: BannerCreate;
}

export interface SubSourcesConfig {
    enabledSources: CampaignSubSource[];
    originalSourcesConfig: SourceConfigDto[];
}

export interface SourceConfigDto {
    apiSource: CampaignSource;
    subSource: CampaignSubSource;
    enabled: boolean;
    disableReason: CampaignSubSourceDisableReason | null;
}

export interface SourceConfigToDto {
    apiSource: CampaignSource;
    subSources: CampaignSubSource[];
}

export interface BannerCreate {
    bannerImages: BannerImagesData[];
    title: string;
    text: string;
    url: string;
    bannerVideo: BannerVideo | null;
    classificationCodeId: string | null;
    isAdvertisement: boolean;
    bannedAsAdvertisement: boolean;
}

export interface Banner extends BannerCreate {
    id: number;
}

export interface VacancyInfoRequirement {
    clickmeUser: boolean;
    profRoleIds: number[];
    areaId: Nullable<number>;
    status: string;
    text: string;
    title: string;
    url: string;
    vacancyAreaId: number;
    vacancyAreaPath: string;
}
